<script>
import { mapActions, mapState } from 'vuex';

export default {
  name: "DragAndDrop",
  data() {
    return {
      fileName: null,
      fileSize: null,
      fileType: null,
      errorMessage: null,
      validFileTypes: [
        'text/plain',
        'text/markdown',
        'application/pdf',
        'text/html',
        'text/xml',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/csv',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/xml',
        'application/epub+zip',
        'message/rfc822',
        'application/vnd.ms-outlook'
      ],
    }
  },
  computed: {
    ...mapState('knowledge', ['uploadedFiles', 'isUploaded']),
    acceptedFileTypes() {
      return [...this.validFileTypes, '.md'].join(',');
    },
    uploadedFilesCount() {
      return this.uploadedFiles ? this.uploadedFiles.length : 0;
    },
    totalFileSize() {
      if (!this.uploadedFiles) return 0;
      return this.uploadedFiles.reduce((total, file) => total + file.size, 0);
    }
  },
  methods: {
    ...mapActions('knowledge', ['handleFilesUpload', 'handleDrop', 'handleRemoveFile', 'removeUploadedFileAt', 'updateIsUploaded']),
    handleDragOver(event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    },
    triggerFileSelect() {
      this.$refs.fileInput.click();
    },
    triggerFileUpload(event) {
      let files = event.target.files;
      
      if (files && files.length > 0) {
        // Convert FileList to Array for easier handling
        const filesArray = Array.from(files);
        
        // Check file types
        const isValidFile = (file) => {
          const extension = file.name.split('.').pop().toLowerCase();

          return (
            this.validFileTypes.includes(file.type) ||
            extension === 'md' ||
            (file.type === 'text/plain' && extension === 'md') ||
            file.type === '' // some browsers
          );
        };

        const invalidFiles = filesArray.filter(file => !isValidFile(file));
        if (invalidFiles.length > 0) {
          this.errorMessage = 'One or more files have invalid types. Please upload valid files.';
          this.$refs.fileInput.value = '';
          return;
        }
        
        // Reset error message
        this.errorMessage = null;
        
        // Update files in store
        this.handleFilesUpload(filesArray);
        this.updateIsUploaded(true);
      } else {
        console.error('File upload failed.');
      }
    },
  }
};
</script>

<template>
  <div>
    <div
      class="drop-zone bg-slate-200 dark:bg-slate-800 max-w-xl"
      @dragover.prevent.stop="handleDragOver"
      @drop.prevent.stop="handleDrop"
      @click="triggerFileSelect"
      tabindex="0"
      role="button"
    >
      <input
        type="file"
        ref="fileInput"
        class="hidden"
        @change="triggerFileUpload"
        :accept="acceptedFileTypes"
        multiple
        data-testid="knowledge_file_input"
      />
      <p>{{ $t('KNOWLEDGE.DRAGANDDROP.DROP_ZONE_TEXT') }} <span class="underline text-woot-500 cursor-pointer">{{ $t('KNOWLEDGE.DRAGANDDROP.DROP_ZONE_CLICK') }}</span></p>
      <br>
      <p class="text-xs text-slate-500 dark:text-slate-300">
        {{ $t('KNOWLEDGE.DRAGANDDROP.SUPPORTED_FORMATS') }}
      </p>
      <div v-if="errorMessage" class="mt-2 text-red-500">{{ $t('KNOWLEDGE.DRAGANDDROP.INVALID_FILE_ERROR') }}</div>
    </div>

    <div v-if="isUploaded" class="mt-4">
      <div v-if="uploadedFilesCount === 1" class="border-2 bg-slate-200 dark:bg-slate-800 flex rounded-md px-5 py-2 justify-between max-w-xl items-center">
        <fluent-icon icon="file-upload" class="h-[30px] w-[30px]"/>
        <span>
          <h1>{{ uploadedFiles[0].name }}</h1>
          <p class="font-light">{{ (uploadedFiles[0].size / 1000000).toFixed(2) }} MB</p>
        </span>
        <button :aria-label="$t('KNOWLEDGE.DRAGANDDROP.REMOVE_FILE')" class="hover:bg-slate-300 dark:hover:bg-slate-700 p-2 rounded-full" @click="handleRemoveFile">
          <fluent-icon icon="delete" />
        </button>
      </div>

      <div v-else class="border-2 bg-slate-200 dark:bg-slate-800 rounded-md px-5 py-3 max-w-xl">
        <div class="flex justify-between items-center mb-3">
          <div>
            <h1 class="text-lg font-medium">{{ uploadedFilesCount }} {{ $t('KNOWLEDGE.DRAGANDDROP.FILES_SELECTED') }}</h1>
            <p class="font-light">{{ $t('KNOWLEDGE.DRAGANDDROP.TOTAL_SIZE') }} {{ (totalFileSize / 1000000).toFixed(2) }} {{ $t('KNOWLEDGE.DRAGANDDROP.MB_UNIT') }}</p>
          </div>
          <button :aria-label="$t('KNOWLEDGE.DRAGANDDROP.REMOVE_ALL_FILES')" class="hover:bg-slate-300 dark:hover:bg-slate-700 p-2 rounded-full" @click="handleRemoveFile">
            <fluent-icon icon="delete" />
          </button>
        </div>

        <div class="max-h-40 overflow-y-auto">
          <div v-for="(file, index) in uploadedFiles" :key="index" class="flex items-center py-1 border-t border-slate-300 dark:border-slate-700">
            <fluent-icon icon="document" class="h-[18px] w-[18px] mr-2"/>
            <span class="flex-1 truncate">{{ file.name }}</span>
            <span class="text-sm text-slate-500 mr-2">{{ (file.size / 1000000).toFixed(2) }} MB</span>
            <button :aria-label="$t('KNOWLEDGE.DRAGANDDROP.REMOVE_FILE')" class="hover:bg-slate-300 dark:hover:bg-slate-700 p-1 rounded-full" @click="removeUploadedFileAt(index)">
              <fluent-icon icon="dismiss" class="h-[16px] w-[16px]" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .drop-zone {
    border-radius: 5px;
    padding: 20px;
    text-align: center;
    cursor: pointer;
  }
  .hidden {
    display: none;
  }
</style>